import { Empty } from '../ui'
import { ScreenHead } from '../components/ScreenHead'
import { fmt, iso } from '../lib/calendar'
import { useNews } from '../lib/queries'
import type { NewsRow } from '../lib/types'
import { Loading, Failed } from './Stub'
import { tidyNewsText } from '../../../src/newsText'

/**
 * Everything the city is doing to itself: disruptions, closures, marches,
 * elections.
 *
 * The destination H4 needed and did not have. WP 2.7 capped Home's News at
 * three items — it was 124, 85% of the screen — which made the section
 * readable and made items 4 onwards unreachable, since there was nowhere for
 * a "More" to point. This is that somewhere: the same rendering the old Home
 * block had, moved rather than rewritten, with no cap on it.
 *
 * It is a route and not a tab (`EXTRA_ROUTES`, the mechanism WP 2.3 added for
 * `#/dev/ui`): a sixth icon in the bottom bar for something read once a week
 * is exactly the busyness N1 asked this phase to remove.
 */

/** Read in the order they are worth reading. Phase 8b's citywide lanes lead —
 *  a disruption or a march is timelier than an opening — with the
 *  newsletter-era pair kept last under their original labels. */
const NEWS_CATEGORIES: { key: NewsRow['category']; label: string }[] = [
  { key: 'transport', label: 'Transport' },
  { key: 'closure', label: 'Closures' },
  { key: 'event', label: 'Big events' },
  { key: 'election', label: 'Elections' },
  { key: 'openings', label: 'Opening and closing' },
  { key: 'culture', label: 'Culture' },
]

/**
 * A news row's date, right-aligned against its title: a single day gets a
 * weekday ("Sat 27 Sept"), a range drops it ("27 Sept – 3 Oct") the same way
 * `whenText` does for a run, and a row with no `affects_from` at all falls
 * back to the day the Worker saw it.
 *
 * An ongoing BVG disruption keeps its original, sometimes years-old,
 * `affects_from` — that is what "still happening" costs when nothing
 * bounds it — and printing it as if it were this row's date range would
 * read as broken rather than as "since 2022". `today` is what tells the two
 * apart from a merely old `published_on`, which is never this stale.
 */
export function newsDateText(n: NewsRow, today: string): string {
  if (!n.affects_from || n.affects_from < today) return fmt(n.published_on, { day: 'numeric', month: 'short' })
  if (!n.affects_to || n.affects_to === n.affects_from) {
    return fmt(n.affects_from, { weekday: 'short', day: 'numeric', month: 'short' })
  }
  const a = fmt(n.affects_from, { day: 'numeric', month: 'short' })
  const b = fmt(n.affects_to, { day: 'numeric', month: 'short' })
  return `${a} – ${b}`
}

/** Still worth reading: the 14-day window is already the query (`useNews`),
 *  and this is the second half of it — a disruption or a march whose own
 *  `affects_to` has gone by is stale even inside that window. */
export const currentNews = (news: NewsRow[], today: string) =>
  news.filter(n => !n.affects_to || n.affects_to >= today)

// WP 10.7: City has no `ScreenHead` at all before this — 10.1 moved the
// account button out of the wordmark bar and into each tab's own head, and
// this route (reached from Today, not a tab) never got one, so its account
// button was unreachable on every state, not only the loaded one.
const TITLE = <h1 className="screen-title">City</h1>

export function City() {
  const today = iso(new Date())
  const q = useNews()

  if (q.isPending) return <><ScreenHead lead={TITLE} /><Loading /></>
  if (q.error) return <><ScreenHead lead={TITLE} /><Failed error={q.error.message} /></>

  const news = currentNews(q.data ?? [], today)

  return (
    <>
      <ScreenHead lead={TITLE} />
      {news.length === 0
        ? <Empty title="Nothing going on in the city." />
        : NEWS_CATEGORIES.map(cat => {
          const items = news.filter(n => n.category === cat.key)
          if (!items.length) return null
          return (
            <div key={cat.key} className="newsgroup">
              <div className="newscat">{cat.label}</div>
              {items.map(n => (
                <div className="newsitem" key={n.id}>
                  <div className="newsitem__head">
                    <b>
                      {n.category === 'transport' && n.lines.length > 0 && (
                        <span className="newslines">
                          {n.lines.map(l => <span className="newsline" key={l}>{l}</span>)}
                        </span>
                      )}
                      {/* `tidyNewsText`: a stored row's own source hyphenation
                          and empty-place comma, repaired as drawn — see
                          `src/newsText.ts`. */}
                      {n.url
                        ? <a href={n.url} target="_blank" rel="noopener noreferrer">{tidyNewsText(n.title)}</a>
                        : tidyNewsText(n.title)}
                    </b>
                    <span className="newsdate">{newsDateText(n, today)}</span>
                  </div>
                  {n.summary && <p>{tidyNewsText(n.summary)}</p>}
                </div>
              ))}
            </div>
          )
        })}
    </>
  )
}
